import React, {FC, useState} from "react";
import {Button, Modal, Tooltip, notification} from "antd";
import {useMutation} from "@apollo/client";
import {
    RETRY_MOLLIE_PAYMENT_MANUALLY,
    GET_MOLLIE_RETRY_QUEUE_ENTRIES,
} from "./mollieRetryQueue.graphql";
import i18n from "../../services/i18n";

type RetryButtonProps = {
    spId: string;
    reasonCode?: string;
    nonRetriable: boolean;
};

/**
 * Extrahiert HIGH-3 Error-Code aus Apollo-GraphQL-Error.
 * Liefert passenden i18n-Text, fallback auf generische errorMessage.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractErrorCode(ge: any): string | undefined {
    if (!ge) return undefined;
    // Apollo server UserInputError platziert den spezifischen Code in extensions.exception.code
    // (extensions.code wird auf "BAD_USER_INPUT" gesetzt — generisch und nicht verwendbar).
    if (ge.extensions && ge.extensions.exception && ge.extensions.exception.code) {
        return ge.extensions.exception.code;
    }
    // Fallback fuer direkt in extensions.code abgelegte Custom-Codes (nicht-UserInputError-Pfad)
    if (ge.extensions && ge.extensions.code && ge.extensions.code !== "BAD_USER_INPUT") {
        return ge.extensions.code;
    }
    // Message-Pattern-Fallback (Server wirft "retry_rejected_already_paid: <human>" o.ae.)
    if (typeof ge.message === "string") {
        const match = ge.message.match(/retry_rejected_[a-z_]+/);
        if (match) return match[0];
    }
    return undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveErrorMessage(err: unknown): string {
    const codes = i18n.containers.payments.MollieRetryQueue.errorCodes;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyErr = err as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const graphQLErrors: any[] = (anyErr && anyErr.graphQLErrors) || [];
    for (const ge of graphQLErrors) {
        const code = extractErrorCode(ge);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (code && (codes as any)[code]) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (codes as any)[code]();
        }
    }
    // Fallback: check top-level message for error code pattern (HIGH-3)
    const msg = err instanceof Error ? err.message : String(err);
    const match = msg.match(/retry_rejected_[a-z_]+/);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (match && (codes as any)[match[0]]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (codes as any)[match[0]]();
    }
    return i18n.containers.payments.MollieRetryQueue.errorMessage({message: msg});
}

/**
 * Phase 8 (RETRY-04) — Wiederverwendbare Retry-Button-Komponente.
 *
 * Wird in zwei UI-Stellen eingesetzt (D-11):
 *   1. Admin-Route `/mollie-retry-queue` (MollieRetryQueueColumns.tsx)
 *   2. Booking-Details-View (PaymentsListColumns.tsx, Spalte `mollieStatus`)
 *
 * Umsetzt D-12/D-13/D-14:
 *   - Modal.confirm mit okType="danger" und 10 €-Kostenwarnung
 *   - Optimistic-Disable: useState-Flag synchron im onOk gesetzt,
 *     zusammen mit Apollo `loading` und Server-Idempotency-Key (3-Layer)
 *   - Bei nonRetriable=true: Button disabled mit Tooltip (Reason-Code-Text)
 *   - Bei Mutation-Success: refetchQueries aktualisiert die Liste
 *   - Bei Mutation-Error: HIGH-3 error codes (retry_rejected_*) werden
 *     ueber resolveErrorMessage auf spezifische i18n-Texte gemappt
 */
export const RetryButton: FC<RetryButtonProps> = ({spId, reasonCode, nonRetriable}) => {
    const [optimisticDisabled, setOptimisticDisabled] = useState(false);
    const [retryMutation, {loading}] = useMutation(RETRY_MOLLIE_PAYMENT_MANUALLY, {
        refetchQueries: [{query: GET_MOLLIE_RETRY_QUEUE_ENTRIES}],
    });

    const disabled = nonRetriable || optimisticDisabled || loading;

    const handleClick = () => {
        if (disabled) return;
        Modal.confirm({
            title: i18n.containers.payments.MollieRetryQueue.confirmTitle(),
            content: (
                <div>
                    <p>{i18n.containers.payments.MollieRetryQueue.confirmCostWarning()}</p>
                    <p style={{marginTop: 8}}>
                        <strong>
                            {i18n.containers.payments.MollieRetryQueue.confirmAreYouSure()}
                        </strong>
                    </p>
                </div>
            ),
            okText: i18n.containers.payments.MollieRetryQueue.confirmOkLabel(),
            cancelText: i18n.containers.payments.MollieRetryQueue.confirmCancelLabel(),
            okType: "danger",
            onOk: async () => {
                // Optimistic-Disable synchron setzen, bevor die Mutation startet
                setOptimisticDisabled(true);
                try {
                    await retryMutation({variables: {scheduledPaymentId: spId}});
                    notification.success({
                        message: i18n.containers.payments.MollieRetryQueue.successMessage(),
                    });
                    // Bei Success bleibt der Button disabled — der Eintrag
                    // verschwindet durch refetchQueries ohnehin aus der Liste.
                } catch (err) {
                    // HIGH-3: Error-Code-spezifische Message via resolveErrorMessage
                    notification.error({
                        message: resolveErrorMessage(err),
                    });
                    setOptimisticDisabled(false);
                }
            },
        });
    };

    if (nonRetriable) {
        return (
            <Tooltip
                title={i18n.containers.payments.MollieRetryQueue.nonRetriableTooltip({
                    code: reasonCode || "?",
                })}
            >
                <Button danger disabled>
                    {i18n.containers.payments.MollieRetryQueue.retryButtonLabel()}
                </Button>
            </Tooltip>
        );
    }

    return (
        <Button danger onClick={handleClick} disabled={disabled} loading={loading}>
            {i18n.containers.payments.MollieRetryQueue.retryButtonLabel()}
        </Button>
    );
};

export default RetryButton;
